import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { useMutation, useQuery } from '@apollo/client';
import { CenteredLoader, ErrorHandler, captureException } from '@playbookux/playbook-web-core';
import qs from 'qs';
import { CREATE_SCHEDULED_TESTER_PUBLIC, GET_PROJECT_BY_ID } from './graphql';
import ScheduleForm from './schedule-form';
import { setInitialState } from '../../../core/actions';

import {
  Container,
} from './styles';
import ParticipantsSource from '../../../core/constants/participantsSource';

const ScheduleConfirmation = (props) => {
  const {
    location: {
      state = {},
    },
    history,
    onSetInitialState,
  } = props;
  const { search } = history.location;
  const parsed = qs.parse(search, { ignoreQueryPrefix: true });
  // the parameter guest in the schedule link is to indicate that the link was sent from the admin panel
  const guest = _.get(parsed, 'guest', false) === 'true';
  const [createScheduledPublic, { loading: loadingMutation, error: errorMutation }] = useMutation(CREATE_SCHEDULED_TESTER_PUBLIC);
  const [openModal, setOpenModal] = useState(false);
  const [originalTestSession, setOriginalTestSession] = useState({});
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    if (_.isEmpty(state)) {
      history.push('/');
    }
  }, [history, state]);

  const {
    projectId = '',
    segmentId = '',
    date: startAt = '',
    device = '',
    timeZone = '',
  } = state;

  const {
    data: projectData = {},
    loading: projectDataLoading,
    refetch,
  } = useQuery(GET_PROJECT_BY_ID, {
    variables: {
      projectId,
    },
    skip: !projectId,
  });

  useEffect(() => {
    refetch();
  }, [projectId, refetch]);

  const isUnhandledError = (error) => error?.message && error.message !== 'time_slot_not_available'
    && error.message !== 'has_already_schedule' && error.message !== 'study_fully_scheduled';

  const handleError = (ex) => {
    const error = (ex && ex.graphQLErrors[0].message)
      ? ex.graphQLErrors[0].message
      : 'Error while sending the form';

    if (error === 'study_fully_scheduled') {
      setErrorMessage({ message: 'This study is fully scheduled.' });
    }

    if (error === 'time_slot_not_available') {
      onSetInitialState();
      history.push(`/schedule?segment_id=${segmentId}&concurrentError=${true}`);
    }

    if (error === 'has_already_schedule') {
      setOriginalTestSession({
        startAt: ex.graphQLErrors[0].extensions.start_at,
        endAt: ex.graphQLErrors[0].extensions.end_at,
      });
      setOpenModal(true);
    }
  };

  const handleSubmit = async (values) => {
    try {
      const { forceReschedule } = values;

      if (!forceReschedule && !_.isEmpty(originalTestSession)) {
        const newState = {
          ...state,
          originalTestSession,
          forceReschedule,
        };

        onSetInitialState();
        history.push({
          pathname: '/schedule/summary',
          state: newState,
        });

        return;
      }

      const response = await createScheduledPublic({
        variables: {
          ...values,
          projectId,
          segmentId,
          startAt,
          guest,
        },
      });

      const { data, error } = response;

      if (!_.isEmpty(error)) {
        setErrorMessage(error);
        captureException('SCHEDULE_CONFIRMATION_PUBLIC_HANDLE_SUBMIT_FAILED', error);
      } else {
        onSetInitialState();
        history.push({
          pathname: '/schedule/summary',
          state: {
            ...state,
            forceReschedule,
          },
          data,
        });
      }
    } catch (ex) {
      handleError(ex);
    }
  };

  const handleClose = () => {
    setOriginalTestSession({});
    setOpenModal(false);
  };

  const handleBack = () => {
    if (projectData && projectData.project && projectData.project.participantsSource) {
      history.push(`/schedule?segment_id=${segmentId}&participants_source=${projectData.project.participantsSource}`);
    } else {
      history.push(`/schedule?segment_id=${segmentId}`);
    }
  };

  if (errorMessage || isUnhandledError(errorMutation)) {
    return (
      <Container>
        <ErrorHandler
          error={errorMessage || errorMutation}
          defaultMessage="Error while confirming schedule."
        />
      </Container>
    );
  }

  if (projectDataLoading) {
    return <CenteredLoader />;
  }

  const { project } = projectData;
  const company = project?.company;

  const isRYO = project?.participantsSource === ParticipantsSource.COMPANY;
  const collectTesterName = !(isRYO && !company?.collectByoTestersInfo);

  return (
    <ScheduleForm
      date={startAt}
      device={device}
      loading={loadingMutation}
      onSubmit={handleSubmit}
      originalTestSession={originalTestSession}
      onClose={handleClose}
      onBack={handleBack}
      open={openModal}
      timeZone={timeZone}
      collectTesterName={collectTesterName}
    />
  );
};

ScheduleConfirmation.propTypes = {
  location: PropTypes.shape({
    state: PropTypes.shape({
      date: PropTypes.string,
      device: PropTypes.string,
      timeZone: PropTypes.string,
      projectId: PropTypes.string,
      segmentId: PropTypes.string,
    }),
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func,
    location: PropTypes.shape({
      search: PropTypes.string,
    }),
  }).isRequired,
  onSetInitialState: PropTypes.func.isRequired,
  authentication: PropTypes.shape({
    scope: PropTypes.string.isRequired,
  }).isRequired,
};

const mapStateToProps = ({ authentication }) => ({
  authentication,
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
  onSetInitialState: setInitialState,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(ScheduleConfirmation);
